import TableAuditRenderer from './audit-renderers/TableAuditRenderer';
import OpportunityAuditRenderer from './audit-renderers/OpportunityAuditRenderer';
import ScreenshotAuditRenderer from './audit-renderers/ScreenshotAuditRenderer';
import FilmstripAuditRenderer from './audit-renderers/FilmstripAuditRenderer';
import DiagnosticAuditRenderer from './audit-renderers/DiagnosticAuditRenderer';
import NumericAuditRenderer from './audit-renderers/NumericAuditRenderer';
import CriticalRequestChainsAuditRenderer from './audit-renderers/CriticalRequestChainsAuditRenderer';
import ErrorAuditRenderer from './audit-renderers/ErrorAuditRenderer';
import DefaultAuditRenderer from './audit-renderers/DefaultAuditRenderer';
import { Accordion, Card, Col, ProgressBar, Row } from 'react-bootstrap';
import ScoreDisplayRenderer, {
  colorByScore,
  iconByScore
} from './audit-renderers/ScoreDisplayRenderer';
import { unitTransformers } from './utils';
import bg1 from '@/assets/img/icons/spot-illustrations/corner-1.png';
import MarkdownRenderer from './audit-renderers/MarkdownRenderer';
import MetricsCard from './audit-renderers/MetricsCard';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import SubtleBadge from '@/components/common/SubtleBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar from '@/components/common/Avatar';
import ImageGallery from './ImageGallery';
import type {
  LighthouseAudit,
  LighthouseAuditRef,
  LighthouseCategory,
  LighthouseDetails,
  ReportLanguage
} from '@/app/PageSpeed/api/types/analysisApiTypes';
import type { MouseEvent } from 'react';
import SpeedAiAdvice from './SpeedAiAdvice';

type AuditGroupTitleKey =
  | 'notApplicableAuditsGroupTitle'
  | 'manualAuditsGroupTitle'
  | 'passedAuditsGroupTitle';

const AUDIT_GROUP_TITLES: Record<
  ReportLanguage,
  Record<AuditGroupTitleKey, string>
> = {
  en: {
    notApplicableAuditsGroupTitle: 'Not Applicable',
    manualAuditsGroupTitle: 'Additional items to manually check',
    passedAuditsGroupTitle: 'Passed Audits'
  },
  fa: {
    notApplicableAuditsGroupTitle: 'قابل اجرا نیست',
    manualAuditsGroupTitle: 'موارد اضافی برای بررسی دستی',
    passedAuditsGroupTitle: 'معیارها موفق'
  }
};

const resolveAuditGroupTitle = (
  analysisData: LighthouseDetails,
  key: AuditGroupTitleKey,
  language: ReportLanguage
) => {
  const fallback = AUDIT_GROUP_TITLES[language][key];
  const fromReport = analysisData.i18n?.rendererFormattedStrings?.[key];
  if (typeof fromReport !== 'string' || !fromReport.trim()) {
    return fallback;
  }
  if (
    language === 'fa' &&
    fromReport.trim().toLowerCase() ===
      AUDIT_GROUP_TITLES.en[key].toLowerCase()
  ) {
    return fallback;
  }
  return fromReport;
};

type Props = {
  refs: LighthouseAuditRef[];
  cat: LighthouseCategory;
  analysisData: LighthouseDetails;
  handleImageClick?: (event: MouseEvent<HTMLImageElement>) => void;
  direction?: 'rtl' | 'ltr';
  language?: ReportLanguage;
};

const AuditsRenderers = ({
  refs,
  cat,
  analysisData,
  direction = 'ltr',
  language = direction === 'rtl' ? 'fa' : 'en'
}: Props) => {
  const sortedRef = [...refs].sort(
    (a, b) => (b?.weight || 0) - (a?.weight || 0)
  );
  const { groupedAudits, passedAudits, notApplicable, manuals } =
    sortedRef.reduce(
      (
        acc: {
          groupedAudits: Record<string, LighthouseAudit[]>;
          passedAudits: LighthouseAudit[];
          notApplicable: LighthouseAudit[];
          manuals: LighthouseAudit[];
        },
        ref
      ) => {
        const audit = analysisData.audits?.[ref.id];
        const group = ref.group || 'Other';
        if (audit) {
          if (audit.scoreDisplayMode === 'notApplicable') {
            acc.notApplicable.push(audit);
          } else if (audit.scoreDisplayMode === 'manual') {
            acc.manuals.push(audit);
          } else if (
            (audit.score ?? 0) >= 0.9 &&
            cat.id !== 'pwa' &&
            (audit.scoreDisplayMode === 'metrics' ||
              audit.scoreDisplayMode === 'metricSavings')
          ) {
            acc.passedAudits.push(audit);
          } else {
            if (!acc.groupedAudits[group]) {
              acc.groupedAudits[group] = [];
            }
            acc.groupedAudits[group].push(audit);
            if ((audit.score ?? 0) >= 0.9 && cat.id === 'pwa') {
              acc.passedAudits.push(audit);
            }
          }
        }

        return acc;
      },
      { groupedAudits: {}, passedAudits: [], notApplicable: [], manuals: [] }
    );

  const renderDefaultAudit = (audit: LighthouseAudit) => {
    if (!audit) {
      return <div>Audit data is missing or incomplete</div>;
    }

    return (
      <Accordion dir={direction} className="">
        <Accordion.Item className="p-0" eventKey="0">
          <Accordion.Header className="p-0">
            <div className="d-flex align-items-center w-100">
              <ScoreDisplayRenderer audit={audit} direction={direction} />
              {audit.id && (
                <SpeedAiAdvice
                  auditId={audit.id}
                  url={analysisData.requestedUrl || ''}
                  details={analysisData}
                />
              )}
            </div>
          </Accordion.Header>
          <Accordion.Body>
            {renderAuditContent(audit, analysisData)}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  const renderGroup = (groupLabel: string, audits: LighthouseAudit[]) => {
    if (audits.length === 0) return null;
    switch (groupLabel) {
      case 'hidden':
        return <></>;
      case 'Other':
        return <></>;
      case 'metrics':
        return (
          <>
            <Row className="g-3" key={groupLabel}>
              {audits.map(audit => (
                <Col xs={12} lg={6} xl={4} key={audit.id}>
                  <MetricsCard
                    stat={{
                      title: audit.title,
                      value: audit.displayValue,
                      score: audit.score,
                      tooltip: audit.description,
                      image: bg1,
                      className: 'text-success'
                    }}
                  />
                </Col>
              ))}
              <Col xs={12} lg={12} className="my-3 metrics-screenshot-gallery">
                <Card>
                  <Card.Body>
                    <div className="d-flex gap-4 flex-wrap justify-content-center align-items-cente">
                      <ImageGallery
                        imageList={analysisData?.audits?.[
                          'screenshot-thumbnails'
                        ]?.details?.items?.map(
                          (screenShot: { data?: string }) => screenShot.data
                        )}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <h6>{groupIdToDescription(groupLabel, analysisData)}</h6>
          </>
        );
      case 'load-opportunities':
        return (
          <>
            <Row key={groupLabel}>
              {audits
                .sort((a, b) => (a.score || 0) - (b.score || 0))
                .map(audit => (
                  <Col key={audit.id} xs={12}>
                    <Accordion dir={direction} className="my-1">
                      <Accordion.Item className="p-0" eventKey="0">
                        <Accordion.Header className="p-0 flex-wrap d-flex justify-content-between">
                          <div
                            style={{
                              direction,
                              width: '100%'
                            }}
                            className="fs-0 text-1000 d-flex flex-wrap align-items-center justify-content-between pe-3"
                          >
                            <div className="d-flex bg-danger align-items-center gap-2">
                              <span
                                style={{
                                  color: colorByScore(audit.score)
                                }}
                              >
                                {iconByScore(audit.score)}
                              </span>
                              <MarkdownRenderer content={audit.title} />
                              {audit.id && (
                                <SpeedAiAdvice
                                  auditId={audit.id}
                                  url={analysisData.requestedUrl || ''}
                                  details={analysisData}
                                />
                              )}
                            </div>
                            <div
                              style={{ color: colorByScore(audit.score) }}
                              className="d-flex my-2 fs--1 align-items-center gap-1"
                            >
                              <div style={{ width: 'min(300px, 100%)', flex: '1 1 140px' }}>
                                <ProgressBar
                                  style={{ height: '6px' }}
                                  now={1 - (audit.score || 0)}
                                  max={1}
                                  className="me-3"
                                  variant={
                                    (audit.score ?? 0) >= 0.9
                                      ? 'success'
                                      : (audit.score ?? 0) >= 0.5
                                        ? 'warning'
                                        : 'danger'
                                  }
                                />
                              </div>

                              {unitTransformers(
                                audit.numericValue,
                                audit.numericUnit || ''
                              )}
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          {renderAuditContent(audit, analysisData)}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </Col>
                ))}
            </Row>
            <h6>{groupIdToDescription(groupLabel, analysisData)}</h6>
          </>
        );
      case 'notApplicable':
        return (
          <Card className="my-3" key={groupLabel}>
            <FalconCardHeader
              title={
                <div className="fw-bold d-flex align-items-center gap-2">
                  {resolveAuditGroupTitle(
                    analysisData,
                    'notApplicableAuditsGroupTitle',
                    language
                  )}
                  <SubtleBadge pill className="fs--1" bg="info">
                    {audits.length}
                  </SubtleBadge>
                </div>
              }
              titleTag="h5"
              className="py-3"
              light
            />
            {audits
              .sort((a, b) => (a.score || 0) - (b.score || 0))
              .map(audit => renderDefaultAudit(audit))}

            {analysisData.categoryGroups?.[groupLabel]?.description && (
              <Card.Footer className="fs--1">
                {groupIdToDescription(groupLabel, analysisData)}
              </Card.Footer>
            )}
          </Card>
        );
      case 'manuals':
        return (
          <Card className="my-3" key={groupLabel}>
            <FalconCardHeader
              title={
                <div className="fw-bold d-flex align-items-center gap-2">
                  <Avatar
                    name={
                      <FontAwesomeIcon
                        icon="bolt"
                        size="lg"
                        className="text-primary"
                      />
                    }
                    isExact
                    size="xl"
                    className="border border-3 rounded-circle border-200"
                  />
                  {resolveAuditGroupTitle(
                    analysisData,
                    'manualAuditsGroupTitle',
                    language
                  )}
                  <SubtleBadge pill className="fs--1" bg="info">
                    {audits.length}
                  </SubtleBadge>
                </div>
              }
              titleTag="h5"
              className="py-3"
              light
            />
            {audits.map(audit => renderDefaultAudit(audit))}
            <Card.Footer className="fs--1">
              {cat?.manualDescription ? (
                <MarkdownRenderer content={cat?.manualDescription} />
              ) : (
                ''
              )}
            </Card.Footer>
          </Card>
        );
      case 'passedAudits':
        return (
          <Card className="my-3" key={groupLabel}>
            <FalconCardHeader
              title={
                <div className="fw-bold d-flex align-items-center gap-2">
                  <Avatar
                    name={
                      <FontAwesomeIcon
                        icon="bolt"
                        size="lg"
                        className="text-primary"
                      />
                    }
                    isExact
                    size="xl"
                    className="border border-3 rounded-circle border-200"
                  />
                  {resolveAuditGroupTitle(
                    analysisData,
                    'passedAuditsGroupTitle',
                    language
                  )}
                  <SubtleBadge pill className="fs--1" bg="success">
                    {audits.length}
                  </SubtleBadge>
                </div>
              }
              titleTag="h5"
              className="py-3"
              light
            />
            {audits
              .sort((a, b) => (a.score || 0) - (b.score || 0))
              .map(audit => renderDefaultAudit(audit))}
            {analysisData.categoryGroups?.[groupLabel]?.description && (
              <Card.Footer className="fs--1">
                {groupIdToDescription(groupLabel, analysisData)}
              </Card.Footer>
            )}
          </Card>
        );
      default: {
        const passedAuditsInGroup = audits.filter(
          audit => (audit.score ?? 0) >= 0.9 && audit.score !== null
        );
        const otherAudits = audits.filter(
          audit =>
            ((audit.score ?? 0) >= 0.5 && (audit.score ?? 0) <= 0.9) ||
            audit.score === null
        );
        const redAudits = audits.filter(
          audit => (audit.score ?? 0) < 0.5 && audit.score !== null
        );

        return (
          <Card className="my-3" key={groupLabel}>
            {groupIdToLabel(groupLabel, analysisData)}
            {passedAuditsInGroup
              .sort((a, b) => (a.score || 0) - (b.score || 0))
              .map(audit => renderDefaultAudit(audit))}
            {redAudits
              .sort((a, b) => (a.score || 0) - (b.score || 0))
              .map(audit => renderDefaultAudit(audit))}
            {otherAudits
              .sort((a, b) => (a.score || 0) - (b.score || 0))
              .map(audit => renderDefaultAudit(audit))}
            {analysisData.categoryGroups?.[groupLabel]?.description && (
              <Card.Footer className="fs--1">
                {groupIdToDescription(groupLabel, analysisData)}
              </Card.Footer>
            )}
          </Card>
        );
      }
    }
  };

  return (
    <>
      {Object.entries(groupedAudits).map(([groupLabel, audits]) =>
        renderGroup(groupLabel, audits)
      )}
      {renderGroup('manuals', manuals)}
      {cat.id !== 'pwa' && renderGroup('passedAudits', passedAudits)}
      {cat.id !== 'performance' && renderGroup('notApplicable', notApplicable)}
    </>
  );
};

export default AuditsRenderers;

const renderAuditContent = (
  audit: LighthouseAudit,
  analysisData: LighthouseDetails
) => {
  if (!audit.details) {
    return (
      <div className="fs--1">
        <MarkdownRenderer content={audit.description} />
      </div>
    );
  }
  const { type } = audit.details;
  switch (type) {
    case 'table':
      return (
        <TableAuditRenderer
          analysisData={analysisData}
          key={audit.id}
          audit={audit}
        />
      );
    case 'opportunity':
      return (
        <OpportunityAuditRenderer
          analysisData={analysisData}
          key={audit.id}
          audit={audit}
        />
      );
    case 'screenshot-thumbnails':
      return <ScreenshotAuditRenderer key={audit.id} audit={audit} />;
    case 'filmstrip':
      return <FilmstripAuditRenderer key={audit.id} audit={audit} />;
    case 'diagnostic':
      return <DiagnosticAuditRenderer key={audit.id} audit={audit} />;
    case 'numeric':
      return <NumericAuditRenderer key={audit.id} audit={audit} />;
    case 'critical-request-chains':
      return (
        <CriticalRequestChainsAuditRenderer key={audit.id} audit={audit} />
      );
    case 'error':
      return <ErrorAuditRenderer key={audit.id} audit={audit} />;
    default:
      return <DefaultAuditRenderer key={audit.id} audit={audit} />;
  }
};

const groupIdToLabel = (groupId: string, analysisData: LighthouseDetails) => {
  return (
    <FalconCardHeader
      title={
        <div className="position-relative fw-bold d-flex align-items-center gap-2">
          <Avatar
            name={
              <FontAwesomeIcon icon="bolt" size="lg" className="text-primary" />
            }
            isExact
            size="xl"
            className="border border-3 rounded-circle border-200"
          />
          {analysisData.categoryGroups?.[groupId]?.title}
        </div>
      }
      titleTag="h5"
      className="py-3"
      light
    />
  );
};

const groupIdToDescription = (
  groupId: string,
  analysisData: LighthouseDetails
) => {
  return analysisData.categoryGroups?.[groupId]?.description ? (
    <MarkdownRenderer
      content={analysisData.categoryGroups[groupId]?.description}
    />
  ) : (
    ''
  );
};
